import { TZDate } from "@date-fns/tz";
import { format } from "date-fns";
import { HOT_DEALS_CATEGORY_ID_PREFIX } from "~/constants";
import type { DealChoice, LocationDeal } from "~/graphql-client/generated";
import type {
  Cart,
  CartItem,
  DayOfTheWeek,
  Product,
  ProductCategory,
} from "~/types";

// Counterpart to findDealLegacy: resolves a hot-deal code (plu) against location's deals and checks availability
export function findDeal(
  deals: LocationDeal[],
  cart: Cart,
  plu: string | null,
): { locationDeal: LocationDeal; cartItemLineItemId?: string } | null {
  const deal = deals.find(
    (deal) => plu && deal.plu.toLowerCase() === plu.toLowerCase(),
  );

  if (!deal) return null;

  const cartItem = cart.items.find(
    (item) => item.isDeal && item.productId === deal.id,
  );

  return {
    locationDeal: deal,
    cartItemLineItemId: cartItem?.lineItemId,
  };
}

// A DealAsVoucher (hidden deal, no options) is auto-added to the cart and shown without a price or edit affordance.
export function isDealAsVoucher(deal: LocationDeal): boolean {
  return deal.hidden && deal.options.length === 0;
}

// Legacy counterpart: a hidden Hot Deals product with no options.
export function isLegacyDealAsVoucher(product: Product): boolean {
  return product.tags.includes(HIDDEN_PRODUCT_TAG) && !product.options?.length;
}

// Legacy deals only check the current day
export function dealAvailableTodayLegacy(
  deal: LocationDeal,
  currentDay: DayOfTheWeek,
): boolean {
  if (!deal.availability?.length) return true;

  return Boolean(
    deal.availability.find((period) => period.dayOfTheWeek === currentDay),
  );
}

export function dealWithinPeriod(
  deal: LocationDeal,
  locationTimeZone: string,
): boolean {
  if (!deal.availability?.length) return true;

  const now = TZDate.tz(locationTimeZone);
  const day = dayOfWeek(locationTimeZone);
  const time = now.toTimeString().slice(0, 5);

  return deal.availability.some(
    (period) =>
      period.dayOfTheWeek === day && period.start <= time && period.end > time,
  );
}

export function dealWithinStartEndWindow(
  deal: LocationDeal,
  locationTimeZone: string,
): boolean {
  const now = TZDate.tz(locationTimeZone);

  if (deal.startAt && now < new TZDate(deal.startAt, locationTimeZone))
    return false;
  if (deal.endAt && now > new TZDate(deal.endAt, locationTimeZone))
    return false;

  return true;
}

// Mirrors the backend recursive availability checks on menu products
export function dealAvailable(
  deal: LocationDeal,
  // should be all root-level products on menu
  rootProducts: Product[],
): boolean {
  const choiceAvailable = (choice: DealChoice) =>
    rootProducts.find((p) => p.id === choice.productDef.id)?.available === true;

  return deal.options.every((option) =>
    choicesFulfillable(
      option.minQuantity,
      (option.choices ?? []).map((choice) => ({
        fulfillable: choiceAvailable(choice),
        minQuantity: choice.minQuantity,
        maxQuantity: choice.maxQuantity,
      })),
    ),
  );
}

// Full  recursive counterpart to dealAvailable; calculates availability taking overrides into account (needs full recusrive product data)
export function productFulfillable(product: Product): boolean {
  if (!product.available) return false;

  return (product.options ?? []).every((option) =>
    choicesFulfillable(
      option.minQuantity,
      option.choices.map((choice) => ({
        fulfillable: productFulfillable(choice.product),
        minQuantity: choice.minQuantity,
        maxQuantity: choice.maxQuantity,
      })),
    ),
  );
}

function choicesFulfillable(
  minQuantity: number,
  choices: { fulfillable: boolean; minQuantity: number; maxQuantity: number }[],
): boolean {
  if (choices.some((c) => c.minQuantity > 0 && !c.fulfillable)) return false;

  if (minQuantity <= 0) return true;

  const fulfillableQuantity = choices.reduce(
    (total, c) => (c.fulfillable ? total + c.maxQuantity : total),
    0,
  );

  return fulfillableQuantity >= minQuantity;
}

export function findDealLegacy(
  locationTimeZone: string,
  menu: ProductCategory[],
  cart: Cart,
  plu: string | null,
): { product: Product; cartItemLineItemId?: string } | null {
  const deals =
    menu.find((c) => c.id.startsWith(HOT_DEALS_CATEGORY_ID_PREFIX))?.products ||
    [];

  const product = deals.find(
    (product) =>
      product.plu && plu && product.plu.toLowerCase() === plu.toLowerCase(),
  );

  if (!product) return null;

  const currentDay = dayOfWeek(locationTimeZone);

  if (!productVisibleToday(product, currentDay)) return null;

  const cartItem = cart.items.find((item) => item.productId === product?.id);

  return {
    product: product,
    cartItemLineItemId: cartItem?.lineItemId,
  };
}

export function dayOfWeek(timeZone: string): DayOfTheWeek {
  const now = TZDate.tz(timeZone); // Current date and time in specified timezone
  return format(now, "EEEEEEE").toUpperCase() as DayOfTheWeek;
}

export function productVisibleToday(
  product: Product,
  currentDay: DayOfTheWeek,
): boolean {
  if (!product.visibility?.length) return true;

  return Boolean(
    product.visibility.find((vis) => vis.dayOfTheWeek === currentDay),
  );
}

const HIDDEN_PRODUCT_TAG = "ui-hint:product:hidden";

// Legacy deals (Hot Deals category products) opt into exclusivity with this tag.
const EXCLUSIVE_LEGACY_DEAL_TAG = "exclusive-deal";

export function isLegacyDealExclusive(product: Product): boolean {
  return product.tags.includes(EXCLUSIVE_LEGACY_DEAL_TAG);
}

export interface SharedDealFields {
  id: string;
  title: string;
}

interface DealsInCartView {
  // Cart line items, one per matching line, for removal
  lineItems: CartItem[];
  // Distinct deals (deduped by id), for display (only using the shared properties between LocationDeal and legacyDeal, i.e. Product)
  deals: SharedDealFields[];
  // Total units, counting quantity (a deal at quantity 2 counts as 2)
  count: number;
}

/**
 * Partitions the cart's deal line items into exclusive and non-exclusive buckets.
 * Each bucket exposes its line items (for removal), the distinct deals among
 * them (deduped by id, for display), and a quantity-aware `count`. A line item whose deal
 * can't be resolved counts toward `nonExclusive` but adds no entry to its `deals`.
 */
function partitionCartDeals(
  items: CartItem[],
  deals: LocationDeal[],
  legacyDeals: Product[],
): { exclusive: DealsInCartView; nonExclusive: DealsInCartView } {
  const dealById = new Map(deals.map((d) => [d.id, d] as const));
  const legacyDealById = new Map(legacyDeals.map((d) => [d.id, d] as const));

  const exclusive: DealsInCartView = { lineItems: [], deals: [], count: 0 };
  const nonExclusive: DealsInCartView = { lineItems: [], deals: [], count: 0 };
  // A deal's exclusivity is fixed, so each deal id only ever lands in one bucket — a single
  // shared set is enough to dedupe `deals` across both.
  const seenDeals = new Set<string>();

  for (const item of items) {
    let deal: SharedDealFields | undefined;
    let isExclusive: boolean;

    if (item.isDeal) {
      // Unresolved deals still count (as non-exclusive) so they're enforced and removable.
      const locationDeal = dealById.get(item.productId);
      deal = locationDeal;
      isExclusive = locationDeal?.exclusive ?? false;
    } else {
      const legacyDeal = legacyDealById.get(item.productId);
      if (!legacyDeal) continue;
      deal = legacyDeal;
      isExclusive = isLegacyDealExclusive(legacyDeal);
    }

    // Only the first line item for a given deal adds it to `deals`.
    const isFirstSeen = deal !== undefined && !seenDeals.has(deal.id);
    if (deal) seenDeals.add(deal.id);

    if (isExclusive) {
      exclusive.lineItems.push(item);
      exclusive.count += item.quantity;
      if (deal && isFirstSeen) exclusive.deals.push(deal);
    } else {
      nonExclusive.lineItems.push(item);
      nonExclusive.count += item.quantity;
      if (deal && isFirstSeen) nonExclusive.deals.push(deal);
    }
  }

  return { exclusive, nonExclusive };
}

/**
 * Validates the cart's exclusive-deal rule: an exclusive deal must be the only deal in
 * the cart, while non-exclusive deals may coexist with each other. When invalid, returns
 * the distinct exclusive deals and any non-exclusive deals present so the caller can build
 * a message tailored to the conflict (multiple exclusives, an exclusive mixed with other
 * deals, or both).
 */
export function checkExclusiveDeals(
  items: CartItem[],
  deals: LocationDeal[],
  legacyDeals: Product[],
):
  | { valid: true }
  | {
      valid: false;
      exclusiveDeals: SharedDealFields[];
      nonExclusiveDeals: SharedDealFields[];
    } {
  const { exclusive, nonExclusive } = partitionCartDeals(
    items,
    deals,
    legacyDeals,
  );

  // Invalid when an exclusive deal sits alongside at least one other deal unit:
  // another exclusive deal, the same exclusive deal added twice, or a non-exclusive deal.
  if (exclusive.count >= 1 && exclusive.count + nonExclusive.count >= 2) {
    return {
      valid: false,
      exclusiveDeals: exclusive.deals,
      nonExclusiveDeals: nonExclusive.deals,
    };
  }

  return { valid: true };
}

/**
 * Determines which deals already in the cart must be removed to add a new deal based off exclusivity.
 * An exclusive new deal can't coexist with any other deal, so every cart deal conflicts;
 * a non-exclusive new deal only conflicts with exclusive deals already in the cart.
 * The returned view exposes `lineItems` (to remove), `deals` (to display), and a
 * `count` (zero means no conflict).
 */
export function getConflictingDeals(
  items: CartItem[],
  deals: LocationDeal[],
  legacyDeals: Product[],
  newDealExclusive: boolean,
): DealsInCartView {
  const { exclusive, nonExclusive } = partitionCartDeals(
    items,
    deals,
    legacyDeals,
  );

  // A non-exclusive new deal only conflicts with the cart's exclusive deals.
  if (!newDealExclusive) return exclusive;

  // An exclusive new deal conflicts with every deal in the cart — combine both buckets.
  return {
    lineItems: [...exclusive.lineItems, ...nonExclusive.lineItems],
    deals: [...exclusive.deals, ...nonExclusive.deals],
    count: exclusive.count + nonExclusive.count,
  };
}
